/**
 * A masterclass as a guided journey through a skill.
 *
 * One continuous page that reads like an experienced coach walking another
 * through it: an inline introduction, a stage strip that sticks to the top,
 * the stages themselves, and a close that loops back. Nothing is locked and
 * nothing asks to be answered. Coaches read this while planning, so "what are
 * they struggling with" is something to keep in mind, never a form field.
 *
 * Each stage is an ordered list of BLOCKS, so the copy can change shape without
 * this file changing:
 *
 *   text             paragraphs, **bold** and *italic* via renderInline
 *   split            two side-by-side labelled lines ("For athletes / For coaches")
 *   qa               a question, a bold short answer, the reasoning
 *   lookFor          what to watch for: groups of questions as a grid of cards
 *   takeaway         the coaching takeaway: a bold lead, optional points, text
 *   games            drill cards by title, or a placeholder until chosen
 *   strategyDiagram  one strategy, the options that can carry it out
 *   strategies       lettered strategies (A, B, C) with a jump row, each with
 *                    its own text, coaching focus and games
 *   supplement       an optional resource, collapsed, that never reads as a stage
 *   progression      steps on a constrained-to-open scale (Require, Reward, Remove)
 *
 * Drills come off the drills table by title through the reader's shared cache,
 * play in the same theater as everywhere else, and come back marked once
 * watched.
 *
 * No em dashes.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, ArrowLeftRight, Check, ChevronDown, Eye, RotateCw } from "lucide-react";
import { GOLD } from "../constants/theme";
import { useWatched } from "../hooks/useWatched";
import { DrillTile } from "./SkillTabs";
import { DrillTheater } from "./DrillTheater";
import { DISPLAY, accent, bodyColor } from "./ArticleBlocks";
import { renderInline } from "./inlineMarkdown";

const ff = "'DM Sans', sans-serif";
const mono = "'JetBrains Mono', monospace";
const norm = (t) => String(t || "").trim().toLowerCase();
const pad = (n) => String(n).padStart(2, "0");

/* Room the sticky strip takes, so a jump lands the heading below it. */
const STRIP_OFFSET = 84;

const CSS = `
  .mcj-strip-inner { display: grid; grid-auto-flow: column; grid-auto-columns: 1fr; gap: 6px; }
  .mcj-stage-btn .mcj-cat { display: block; }
  .mcj-grid { display: grid; gap: 14px; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); }
  .mcj-stage, .mcj-anchor { scroll-margin-top: ${STRIP_OFFSET}px; }
  .mcj-close-ctas { display: flex; gap: 10px; flex-wrap: wrap; }
  .mcj-cards3 { display: grid; gap: 10px; grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .mcj-split { display: grid; gap: 10px; grid-template-columns: 1fr 1fr; }
  .mcj-look { display: grid; gap: 10px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .mcj-steps { display: grid; gap: 10px; grid-template-columns: repeat(3, minmax(0, 1fr)); }
  @media (max-width: 640px) {
    .mcj-strip-inner { grid-auto-columns: max-content; overflow-x: auto; scrollbar-width: none;
      -webkit-overflow-scrolling: touch; padding-bottom: 2px; }
    .mcj-strip-inner::-webkit-scrollbar { display: none; }
    .mcj-stage-btn .mcj-cat { display: none; }
    .mcj-grid, .mcj-cards3, .mcj-split, .mcj-look, .mcj-steps { grid-template-columns: 1fr; }
    .mcj-title { font-size: 32px !important; }
    .mcj-tagline { font-size: 22px !important; }
    .mcj-stage-head { font-size: 27px !important; }
    .mcj-close-ctas > * { flex: 1 1 100%; }
  }
`;

/* The nearest ancestor that scrolls. The reader scrolls a column, not the
   window, and the strip needs to listen to whichever it is. */
function scrollParent(el) {
  let n = el?.parentElement;
  while (n && n !== document.body) {
    const oy = getComputedStyle(n).overflowY;
    if (oy === "auto" || oy === "scroll") return n;
    n = n.parentElement;
  }
  return window;
}

const rich = (t) => renderInline(String(t || ""));

function Eyebrow({ children, color, style }) {
  return (
    <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.6, textTransform: "uppercase",
      color, fontFamily: ff, ...style }}>
      {children}
    </div>
  );
}

function Paras({ items = [], dark, size = 15.5, style }) {
  return items.map((p, i) => (
    <p key={i} style={{ margin: i ? "12px 0 0" : 0, fontSize: size, lineHeight: 1.7,
      color: bodyColor(dark), maxWidth: "64ch", ...style }}>
      {rich(p)}
    </p>
  ));
}

function SubHead({ children, C }) {
  return (
    <h3 style={{ margin: "0 0 12px", fontFamily: DISPLAY, fontSize: 21, fontWeight: 700,
      color: C.text, letterSpacing: "-0.01em", lineHeight: 1.25 }}>
      {rich(children)}
    </h3>
  );
}

/* ── Blocks ───────────────────────────────────────────────────────────── */

function Split({ b, C, dark }) {
  const card = (side) => (
    <div style={{ padding: "14px 16px", borderRadius: 12, border: `1px solid ${C.border}`,
      background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)" }}>
      <Eyebrow color={accent(dark)} style={{ marginBottom: 6 }}>{side.label}</Eyebrow>
      <div style={{ fontSize: 15.5, lineHeight: 1.5, fontWeight: 700, color: C.text }}>{rich(side.text)}</div>
    </div>
  );
  return <div className="mcj-split">{card(b.left)}{card(b.right)}</div>;
}

function QA({ b, C, dark }) {
  return (
    <div style={{ padding: "18px 20px", borderRadius: 14, border: `1px solid ${GOLD}33`,
      background: `${GOLD}0A` }}>
      <div style={{ fontFamily: DISPLAY, fontSize: 19, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>
        {b.question}
      </div>
      {b.answer && (
        <div style={{ marginTop: 8, fontSize: 16, fontWeight: 800, color: accent(dark) }}>{b.answer}</div>
      )}
      <div style={{ marginTop: 10 }}><Paras items={b.paras} dark={dark} size={14.5}/></div>
    </div>
  );
}

/** What to look for: a lens, not a checklist, so nothing to tick. */
function LookFor({ b, C, dark }) {
  const groups = b.groups || [];
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
        <Eye size={14} color={accent(dark)}/>
        <Eyebrow color={accent(dark)}>{b.label || "What to look for"}</Eyebrow>
      </div>
      {b.intro && <Paras items={[b.intro]} dark={dark} style={{ marginBottom: 14 }}/>}
      <div className="mcj-look">
        {groups.map((g, i) => (
          <div key={i} style={{ padding: "14px 16px", borderRadius: 12, border: `1px solid ${C.border}`,
            background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              {b.numbered && (
                <span style={{ fontFamily: mono, fontSize: 11, color: accent(dark), flexShrink: 0 }}>{i + 1}</span>
              )}
              <span style={{ fontSize: 14.5, fontWeight: 800, color: C.text, lineHeight: 1.35 }}>{g.title}</span>
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 6 }}>
              {(g.items || []).map((t, j) => (
                <li key={j} style={{ fontSize: 13.5, lineHeight: 1.5, color: C.textMid }}>{rich(t)}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {b.note && (
        <div style={{ marginTop: 12, fontSize: 13.5, lineHeight: 1.6, color: C.textMid, maxWidth: "64ch" }}>
          {rich(b.note)}
        </div>
      )}
    </div>
  );
}

function Takeaway({ b, C, dark }) {
  return (
    <div style={{ paddingLeft: 16, borderLeft: `2px solid ${accent(dark)}` }}>
      <Eyebrow color={accent(dark)} style={{ marginBottom: 6 }}>{b.label || "Coaching takeaway"}</Eyebrow>
      <div style={{ fontFamily: DISPLAY, fontSize: 20, lineHeight: 1.35, fontWeight: 600,
        fontStyle: "italic", color: dark ? "#F5F5F5" : "#151515", maxWidth: "52ch" }}>
        {rich(b.lead)}
      </div>
      {(b.points || []).length > 0 && (
        <ul style={{ margin: "12px 0 0", padding: 0, listStyle: "none", display: "grid", gap: 6 }}>
          {b.points.map((p, i) => (
            <li key={i} style={{ display: "flex", gap: 10, fontSize: 14.5, lineHeight: 1.5, color: C.text }}>
              <span aria-hidden="true" style={{ flexShrink: 0, marginTop: 10, width: 10, height: 1, background: accent(dark) }}/>
              <span>{rich(p)}</span>
            </li>
          ))}
        </ul>
      )}
      {(b.paras || []).length > 0 && (
        <div style={{ marginTop: 10 }}><Paras items={b.paras} dark={dark} size={14.5}/></div>
      )}
    </div>
  );
}

function IntensityKey({ C }) {
  return (
    <span title="Intensity of the challenge, not how advanced the drill is."
      style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textDim, fontFamily: ff }}>
      <span style={{ display: "inline-flex", gap: 3 }}>
        {[0, 1, 2].map(i => <span key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: GOLD }}/>)}
      </span>
      intensity, not difficulty
    </span>
  );
}

/** The drill cards for a set of titles. Same tile as everywhere else. */
function Tiles({ titles = [], g, seen }) {
  const { ready, drillCache, intensity, posterTime, watched, onPlay, C } = g;
  if (!ready) return <div style={{ fontSize: 13, color: C.textDim, padding: "14px 0" }}>Loading the clips...</div>;
  const drills = titles.map(t => drillCache.get(norm(t))).filter(Boolean);
  if (!drills.length) {
    return <div style={{ fontSize: 13, color: C.textDim, padding: "14px 0", fontStyle: "italic" }}>These clips are not available yet.</div>;
  }
  return (
    <div className="mcj-grid">
      {drills.map(d => (
        <DrillTile key={d.id} d={d} C={C} intensity={intensity[norm(d.title)]}
          watched={watched.has(d.id)} posterTime={posterTime[norm(d.title)]}
          note={seen?.[norm(d.title)]} onPlay={() => onPlay(d)}/>
      ))}
    </div>
  );
}

function Games({ b, g, seen }) {
  const { C, intensity = {} } = g;
  const titles = b.titles || [];
  // The key only shows when this block's games are rated; an unrated game
  // shows no meter rather than a made-up one.
  const rated = titles.some(t => intensity[norm(t)] != null);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>{rich(b.label || "Games to try")}</div>
        {rated && <IntensityKey C={C}/>}
      </div>
      {titles.length > 0 ? <Tiles titles={titles} g={g} seen={seen}/> : (
        <div style={{ padding: "18px 16px", borderRadius: 12, border: `1px dashed ${C.border}`,
          fontSize: 13.5, color: C.textDim }}>
          {b.placeholder || "Games coming soon."}
        </div>
      )}
      {b.note && (
        <div style={{ marginTop: 10, fontSize: 13.5, lineHeight: 1.6, color: C.textMid, maxWidth: "64ch" }}>{rich(b.note)}</div>
      )}
    </div>
  );
}

/** One strategy and the options that can carry it out. */
function StrategyDiagram({ b, C, dark }) {
  return (
    <div style={{ padding: "18px 18px 16px", borderRadius: 14, border: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "stretch", gap: 14, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 180px", minWidth: 0 }}>
          <Eyebrow color={C.textDim} style={{ marginBottom: 6 }}>Strategy</Eyebrow>
          <div style={{ padding: "12px 14px", borderRadius: 10, background: `${GOLD}14`,
            border: `1px solid ${GOLD}55`, fontSize: 15.5, fontWeight: 800, color: C.text }}>
            {b.strategy}
          </div>
        </div>
        <div aria-hidden="true" style={{ display: "flex", alignItems: "flex-end", paddingBottom: 12 }}>
          <ArrowRight size={18} color={accent(dark)}/>
        </div>
        <div style={{ flex: "2 1 240px", minWidth: 0 }}>
          <Eyebrow color={C.textDim} style={{ marginBottom: 6 }}>Ways to do it</Eyebrow>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {(b.options || []).map(o => (
              <span key={o} style={{ padding: "11px 13px", borderRadius: 10, border: `1px solid ${C.border}`,
                fontSize: 14, fontWeight: 700, color: C.text }}>{o}</span>
            ))}
          </div>
        </div>
      </div>
      {b.caption && (
        <div style={{ marginTop: 12, fontSize: 13.5, lineHeight: 1.6, color: C.textMid, maxWidth: "64ch" }}>{rich(b.caption)}</div>
      )}
    </div>
  );
}

function Strategies({ b, C, dark, g, onJump }) {
  const items = b.items || [];
  return (
    <div>
      {b.intro && <Paras items={[b.intro]} dark={dark}/>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "14px 0 6px" }}>
        {items.map(it => (
          <button key={it.id} type="button" onClick={() => onJump(`strategy-${it.id}`)}
            style={{ padding: "8px 13px", borderRadius: 999, border: `1px solid ${C.border}`, background: "transparent",
              cursor: "pointer", fontFamily: ff, fontSize: 13, fontWeight: 700, color: C.textMid,
              display: "inline-flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontFamily: mono, fontSize: 11, color: accent(dark) }}>{it.letter}</span>{it.name}
          </button>
        ))}
      </div>
      {items.map(it => (
        <div key={it.id} id={`strategy-${it.id}`} className="mcj-anchor"
          style={{ marginTop: 30, paddingTop: 22, borderTop: `1px solid ${C.border}` }}>
          <Eyebrow color={accent(dark)}>
            {it.letter} <span style={{ color: C.textDim }}>/</span> {it.name}
          </Eyebrow>
          <h3 style={{ margin: "8px 0 12px", fontFamily: DISPLAY, fontSize: 23, fontWeight: 700,
            color: C.text, letterSpacing: "-0.012em", lineHeight: 1.2 }}>{it.headline}</h3>
          <Paras items={it.paras} dark={dark}/>
          {it.focus && (
            <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: "4px 10px", alignItems: "baseline", padding: "12px 14px",
              borderRadius: 10, background: `${GOLD}0D`, border: `1px solid ${GOLD}2E` }}>
              <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2,
                textTransform: "uppercase", color: accent(dark) }}>Coaching focus</span>
              <span style={{ flex: "1 1 240px", minWidth: 0, fontSize: 14.5, lineHeight: 1.5, fontWeight: 600, color: C.text }}>{rich(it.focus)}</span>
            </div>
          )}
          <div style={{ marginTop: 18 }}>
            <Games b={{ label: "Games", titles: it.titles, placeholder: it.placeholder }} g={g}/>
          </div>
        </div>
      ))}
    </div>
  );
}

/** An optional resource. Closed by default, so it reads as a tool to reach
 *  for rather than a stage of its own. */
function Supplement({ b, C, dark, g }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderRadius: 12, border: `1px dashed ${C.border}`, overflow: "hidden" }}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        style={{ width: "100%", textAlign: "left", cursor: "pointer", background: "transparent", border: "none",
          padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, fontFamily: ff }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
            {b.label && (
              <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase",
                color: C.textDim, border: `1px solid ${C.border}`, padding: "1px 7px", borderRadius: 6 }}>{b.label}</span>
            )}
            <span style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>{b.title}</span>
          </div>
          {b.summary && <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.5 }}>{rich(b.summary)}</div>}
        </div>
        <ChevronDown size={16} color={C.textMid}
          style={{ flexShrink: 0, transition: "transform .18s", transform: open ? "rotate(180deg)" : "none" }}/>
      </button>
      {open && (
        <div style={{ padding: "2px 16px 18px" }}>
          <Paras items={b.body} dark={dark} size={14}/>
          {(b.titles || []).length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Games b={{ label: b.titlesLabel || "Examples", titles: b.titles }} g={g}/>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Steps on a scale from constrained to open. The bar underneath is the point:
 *  freedom grows as you move right. */
function Progression({ b, C, dark }) {
  const steps = b.steps || [];
  return (
    <div>
      {b.title && <SubHead C={C}>{b.title}</SubHead>}
      {b.intro && <Paras items={[b.intro]} dark={dark}/>}
      <div className="mcj-steps" style={{ marginTop: 16 }}>
        {steps.map((s, i) => (
          <div key={s.name} style={{ padding: "14px 16px", borderRadius: 12, border: `1px solid ${C.border}`, minWidth: 0,
            background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)" }}>
            <div style={{ fontFamily: mono, fontSize: 11, color: accent(dark) }}>{pad(i + 1)}</div>
            <div style={{ marginTop: 4, fontSize: 13, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: C.text }}>
              {s.name}
            </div>
            <div style={{ marginTop: 6, fontSize: 15, fontWeight: 800, color: accent(dark), lineHeight: 1.35 }}>{s.lead}</div>
            <div style={{ marginTop: 8 }}><Paras items={s.paras} dark={dark} size={13.5}/></div>
          </div>
        ))}
      </div>
      {b.scale && (
        <div aria-hidden="true" style={{ marginTop: 12 }}>
          <div style={{ height: 4, borderRadius: 2,
            background: `linear-gradient(90deg, ${C.border}, ${GOLD})` }}/>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 11,
            fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: C.textDim }}>
            <span>{b.scale[0]}</span><span>{b.scale[1]}</span>
          </div>
        </div>
      )}
      {b.note && (
        <div style={{ marginTop: 16, padding: "12px 14px", borderRadius: 10, border: `1px solid ${C.border}` }}>
          <Eyebrow color={C.textDim} style={{ marginBottom: 6 }}>{b.note.label || "Note"}</Eyebrow>
          <Paras items={b.note.paras} dark={dark} size={14}/>
        </div>
      )}
    </div>
  );
}

function Block({ b, C, dark, g, seen, onJump }) {
  switch (b.kind) {
    // Wrapped: the stage lays blocks out on a grid gap, and bare paragraphs
    // would each take a full block's spacing.
    case "text": return <div><Paras items={b.paras} dark={dark}/></div>;
    case "split": return <Split b={b} C={C} dark={dark}/>;
    case "qa": return <QA b={b} C={C} dark={dark}/>;
    case "lookFor": return <LookFor b={b} C={C} dark={dark}/>;
    case "takeaway": return <Takeaway b={b} C={C} dark={dark}/>;
    case "games": return <Games b={b} g={g} seen={seen}/>;
    case "strategyDiagram": return <StrategyDiagram b={b} C={C} dark={dark}/>;
    case "strategies": return <Strategies b={b} C={C} dark={dark} g={g} onJump={onJump}/>;
    case "supplement": return <Supplement b={b} C={C} dark={dark} g={g}/>;
    case "progression": return <Progression b={b} C={C} dark={dark}/>;
    default: return null;
  }
}

/* Every drill title a block references, for one fetch up front. */
function titlesIn(blocks = []) {
  return blocks.flatMap(b => [
    ...(b.titles || []),
    ...(b.items || []).flatMap(it => it.titles || []),
  ]);
}

/** The sticky strip: the stages, then the loop back. */
function StageStrip({ stages, current, onJump, C, dark, endLabel }) {
  const innerRef = useRef(null);
  /* On a phone the strip scrolls sideways, so keep the current stage in it.
     scrollLeft rather than scrollIntoView, which would also move the page. */
  useEffect(() => {
    const row = innerRef.current;
    const btn = row?.querySelector('[aria-current="step"]');
    if (!row || !btn || row.scrollWidth <= row.clientWidth) return;
    const left = btn.offsetLeft - row.offsetLeft;
    if (left < row.scrollLeft || left + btn.offsetWidth > row.scrollLeft + row.clientWidth) {
      row.scrollTo({ left: Math.max(0, left - 12), behavior: "smooth" });
    }
  }, [current]);
  const items = [...stages.map((s, i) => ({ id: s.id, n: pad(i + 1), name: s.name, sub: s.summary })),
    ...(endLabel ? [{ id: "keep-developing", n: null, name: endLabel, sub: "Revisit any stage" }] : [])];
  return (
    <nav aria-label="Stages" style={{ position: "sticky", top: 0, zIndex: 20, margin: "0 -12px", padding: "10px 12px",
      background: dark ? "#0A0A0A" : "#FFFFFF", borderBottom: `1px solid ${C.border}` }}>
      <div ref={innerRef} className="mcj-strip-inner">
        {items.map((s, i) => {
          const on = s.id === current;
          return (
            <button key={s.id} type="button" className="mcj-stage-btn" onClick={() => onJump(s.id)}
              aria-current={on ? "step" : undefined}
              style={{ textAlign: "left", cursor: "pointer", padding: "8px 10px", borderRadius: 10, fontFamily: ff,
                background: on ? `${GOLD}14` : "transparent", border: `1px solid ${on ? GOLD + "66" : "transparent"}`,
                transition: "background .15s, border-color .15s", minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {s.n ? (
                  <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: on ? accent(dark) : C.textDim }}>{s.n}</span>
                ) : <RotateCw size={12} color={on ? accent(dark) : C.textDim}/>}
                <span style={{ fontSize: 13.5, fontWeight: 800, color: on ? C.text : C.textMid, whiteSpace: "nowrap" }}>{s.name}</span>
                {i < items.length - 1 && (
                  <span aria-hidden="true" className="mcj-cat" style={{ flex: 1, height: 1, minWidth: 8,
                    background: `linear-gradient(90deg, ${on ? GOLD + "88" : C.border}, transparent)` }}/>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export function JourneyLayout({ block, C, dark, fetchDrills, drillCache, completion, publicMode }) {
  const {
    eyebrow, title, tagline, meta, intro = [], note, journeyLabel,
    stages = [], close, intensity = {}, posterTime = {},
  } = block;

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [current, setCurrent] = useState(stages[0]?.id || null);
  const { watched } = useWatched();
  const rootRef = useRef(null);
  const anchors = useRef({});

  const allTitles = useMemo(
    () => [...new Set(stages.flatMap(s => titlesIn(s.blocks)))],
    [stages]
  );

  /* One fetch for every drill on the page, up front, so jumping around and
     opening the optional card never waits. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await fetchDrills(allTitles); } catch { /* each block shows its empty state */ }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [allTitles, fetchDrills]);

  /* Which stage is under the strip: the last one whose top has passed it. */
  const sectionIds = useMemo(() => [...stages.map(s => s.id), ...(close ? ["keep-developing"] : [])], [stages, close]);
  useEffect(() => {
    const scroller = scrollParent(rootRef.current);
    let raf = 0;
    const on = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const top = scroller === window ? 0 : scroller.getBoundingClientRect().top;
        let cur = sectionIds[0] || null;
        for (const id of sectionIds) {
          const el = anchors.current[id];
          if (el && el.getBoundingClientRect().top - top <= STRIP_OFFSET + 40) cur = id;
        }
        // The last section is shorter than the screen, so its top never
        // reaches the strip. At the bottom of the page, it's the current one.
        const sc = scroller === window ? document.scrollingElement : scroller;
        if (sc && sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 4) cur = sectionIds[sectionIds.length - 1];
        setCurrent(cur);
      });
    };
    on();
    scroller.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => {
      cancelAnimationFrame(raf);
      scroller.removeEventListener("scroll", on);
      window.removeEventListener("resize", on);
    };
  }, [sectionIds, ready]);

  const jump = (id) => {
    const el = anchors.current[id] || document.getElementById(id);
    if (!el) return;
    if (anchors.current[id]) setCurrent(id);
    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };

  const g = { ready, drillCache, intensity, posterTime, watched, onPlay: setPlaying, C };
  const display = (size, extra) => ({ fontFamily: DISPLAY, fontWeight: 700, color: C.text,
    letterSpacing: "-0.018em", fontVariationSettings: "'opsz' 100", ...extra, fontSize: size });

  return (
    <div ref={rootRef} style={{ fontFamily: ff }}>
      <style>{CSS}</style>

      {/* ── Introduction ─────────────────────────────────────────────── */}
      <header style={{ padding: "6px 0 26px" }}>
        <Eyebrow color={accent(dark)} style={{ marginBottom: 14 }}>{eyebrow}</Eyebrow>
        <h1 className="mcj-title" style={display(40, { margin: 0, lineHeight: 1.08, textWrap: "balance" })}>{title}</h1>
        {tagline && (
          <div className="mcj-tagline" style={display(28, { fontWeight: 600, fontStyle: "italic", color: accent(dark),
            marginTop: 10, lineHeight: 1.2 })}>{tagline}</div>
        )}
        {meta && <div style={{ marginTop: 14, fontFamily: mono, fontSize: 11.5, color: C.textDim }}>{meta}</div>}

        <div style={{ marginTop: 24 }}><Paras items={intro} dark={dark} size={16.5}/></div>

        {/* The journey at a glance. Each card is a jump; the arrows between
            them run both ways because the stages aren't a ladder. */}
        <div style={{ marginTop: 28 }}>
          {journeyLabel && <Eyebrow color={C.textDim} style={{ marginBottom: 12 }}>{journeyLabel}</Eyebrow>}
          <div className="mcj-cards3">
            {stages.map((s, i) => (
              <button key={s.id} type="button" onClick={() => jump(s.id)}
                style={{ position: "relative", textAlign: "left", cursor: "pointer", fontFamily: ff, padding: "14px 16px",
                  borderRadius: 12, border: `1px solid ${C.border}`, minWidth: 0,
                  background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontFamily: mono, fontSize: 11.5, color: accent(dark) }}>{pad(i + 1)}.</span>
                  <span style={{ fontSize: 13.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: C.text }}>{s.name}</span>
                </div>
                <div style={{ marginTop: 6, fontSize: 13.5, lineHeight: 1.5, color: C.textMid }}>{s.summary}</div>
              </button>
            ))}
          </div>
          {note && (
            <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, lineHeight: 1.6,
              color: C.textMid, padding: "12px 14px", borderRadius: 10, background: `${GOLD}0D`, border: `1px solid ${GOLD}2E` }}>
              <ArrowLeftRight size={15} color={accent(dark)} style={{ flexShrink: 0, marginTop: 3 }}/>
              <span>{rich(note)}</span>
            </div>
          )}
        </div>
      </header>

      <StageStrip stages={stages} current={current} onJump={jump} C={C} dark={dark}
        endLabel={close ? (close.eyebrow || "Keep developing") : null}/>

      {/* ── Stages ───────────────────────────────────────────────────── */}
      {stages.map((s, i) => (
        <section key={s.id} id={`stage-${s.id}`} className="mcj-stage"
          ref={el => { anchors.current[s.id] = el; }}
          style={{ padding: "44px 0 40px", borderBottom: `1px solid ${C.border}` }}>
          <Eyebrow color={accent(dark)}>
            {pad(i + 1)} <span style={{ color: C.textDim }}>/</span> {s.name}
          </Eyebrow>
          <h2 className="mcj-stage-head" style={display(32, { margin: "10px 0 18px", lineHeight: 1.12 })}>{s.headline}</h2>
          <div style={{ display: "grid", gap: 26 }}>
            {(s.blocks || []).map((b, j) => (
              <Block key={j} b={b} C={C} dark={dark} g={g} onJump={jump}/>
            ))}
          </div>
          <button type="button" onClick={() => jump(i < stages.length - 1 ? stages[i + 1].id : "keep-developing")}
            style={{ marginTop: 30, background: "transparent", border: "none", cursor: "pointer", padding: 0, fontFamily: ff,
              fontSize: 13, fontWeight: 700, color: C.textMid, display: "inline-flex", alignItems: "center", gap: 6 }}>
            Next: {i < stages.length - 1 ? `${pad(i + 2)} ${stages[i + 1].name}` : (close?.eyebrow || "Keep developing")}
            <ArrowRight size={13}/>
          </button>
        </section>
      ))}

      {/* ── Keep developing ──────────────────────────────────────────── */}
      {close && (
        <section ref={el => { anchors.current["keep-developing"] = el; }} className="mcj-stage"
          style={{ marginTop: 28, padding: "32px 24px 28px", borderRadius: 16, border: `1px solid ${GOLD}33`,
            background: `linear-gradient(180deg, ${GOLD}10, ${GOLD}03)` }}>
          <Eyebrow color={accent(dark)}>{close.eyebrow}</Eyebrow>
          <h2 className="mcj-stage-head" style={display(30, { margin: "10px 0 14px", lineHeight: 1.12 })}>{close.title}</h2>
          {close.lead && <Paras items={[close.lead]} dark={dark}/>}

          {/* Each "maybe" goes back to the stage it describes. */}
          {(close.maybes || []).length > 0 && (
            <div style={{ display: "grid", gap: 8, marginTop: 16 }}>
              {close.maybes.map((m, i) => {
                const idx = stages.findIndex(s => s.id === m.stage);
                return (
                  <button key={i} type="button" onClick={() => jump(m.stage)}
                    style={{ textAlign: "left", cursor: "pointer", fontFamily: ff, padding: "12px 14px", borderRadius: 10,
                      border: `1px solid ${C.border}`, background: dark ? "rgba(10,10,10,0.5)" : "rgba(255,255,255,0.6)",
                      display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, lineHeight: 1.5, color: C.text }}>{rich(m.text)}</span>
                    {idx >= 0 && (
                      <span style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12,
                        fontWeight: 800, color: accent(dark), whiteSpace: "nowrap" }}>
                        {pad(idx + 1)} {stages[idx].name} <ArrowRight size={12}/>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {(close.body || []).length > 0 && <div style={{ marginTop: 18 }}><Paras items={close.body} dark={dark}/></div>}

          {close.loop?.length > 0 && (
            <div style={{ marginTop: 22, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {close.loop.map((w, i) => (
                <React.Fragment key={w}>
                  <span style={{ fontSize: 13.5, fontWeight: 800, letterSpacing: 1.6, textTransform: "uppercase", color: C.text }}>{w}</span>
                  {i < close.loop.length - 1 && <ArrowRight size={14} color={accent(dark)}/>}
                </React.Fragment>
              ))}
              <RotateCw size={14} color={accent(dark)} aria-hidden="true"/>
            </div>
          )}

          <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
            {close.cta?.hint && !publicMode && (
              <div style={{ fontSize: 13, color: C.textMid, marginBottom: 12, lineHeight: 1.55 }}>{close.cta.hint}</div>
            )}
            <div className="mcj-close-ctas">
              {/* A real route: Training Plans is where a session gets built.
                  Not on the public preview, which has no account. */}
              {close.cta && !publicMode && completion?.leaveTo && (
                <button type="button" onClick={() => completion.leaveTo(close.cta.route)}
                  style={{ padding: "13px 20px", borderRadius: 11, border: "none", background: GOLD, color: "#111",
                    fontFamily: ff, fontSize: 14, fontWeight: 800, cursor: "pointer", display: "inline-flex",
                    alignItems: "center", justifyContent: "center", gap: 7, boxShadow: `0 4px 14px ${GOLD}44` }}>
                  {close.cta.label} <ArrowRight size={15}/>
                </button>
              )}
              {/* Completion means the coach finished reviewing this, not that
                  their players have the skill. Same toggle as every other
                  masterclass. */}
              {completion && (
                <button type="button" onClick={completion.toggle}
                  style={{ padding: "13px 20px", borderRadius: 11, fontFamily: ff, fontSize: 13, fontWeight: 800,
                    letterSpacing: .4, cursor: "pointer", border: `1px solid ${completion.done ? GOLD : C.border}`,
                    background: completion.done ? `${GOLD}18` : "transparent",
                    color: completion.done ? accent(dark) : C.textMid,
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
                  {completion.done ? <><Check size={14} strokeWidth={3}/> Completed</> : "Mark as completed"}
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      <DrillTheater drill={playing} C={C} dark={dark} onClose={() => setPlaying(null)}/>
    </div>
  );
}

export default JourneyLayout;
